"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useState } from "react";
import Link from "next/link";
import { ArrowLeft, ChevronDown } from "lucide-react";
import type { CollectionNavItem } from "@/types/segments";
interface CollectionSidebarLinksProps {
  collections: CollectionNavItem[];
  currentSlug?: string | undefined;
  onNavigate?: (() => void) | undefined;
}

/** Collection navigation that is intentionally separate from faceted controls. */
export default function CollectionSidebarLinks(t0) {
  const $ = _c(28);
  const {
    collections,
    currentSlug,
    onNavigate
  } = t0;
  const [isOpen, setIsOpen] = useState(Boolean(currentSlug));
  const instanceId = useId();
  const headingId = `browse-inventory-${instanceId}`;
  const listId = `inventory-collections-${instanceId}`;
  let t1;
  let t2;
  if ($[0] !== currentSlug) {
    t1 = () => {
      if (currentSlug) {
        setIsOpen(true);
      }
    };
    t2 = [currentSlug];
    $[0] = currentSlug;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (collections.length === 0) {
    return null;
  }
  let t3;
  if ($[3] !== headingId) {
    t3 = <h2 id={headingId} className="t-sr-only">Browse inventory collections</h2>;
    $[3] = headingId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== currentSlug || $[6] !== onNavigate) {
    t4 = currentSlug && <div className="t-border-b t-border-[#e4e0e6] t-px-6 t-py-3"><Link href="/cars" {...onNavigate ? {
        onClick: onNavigate
      } : {}} className="t-inline-flex t-items-center t-gap-2 t-rounded t-text-xs t-font-semibold t-text-automotive-primary hover:t-text-automotive-secondary focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2"><ArrowLeft className="t-h-3.5 t-w-3.5" aria-hidden="true" /><span>Back to all vehicles</span></Link></div>;
    $[5] = currentSlug;
    $[6] = onNavigate;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsOpen(_temp);
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span>Collections</span>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `t-h-4 t-w-4 t-shrink-0 t-text-[#928c96] t-transition-transform ${isOpen ? "t-rotate-180" : ""}`;
  let t8;
  if ($[10] !== t7) {
    t8 = <ChevronDown className={t7} aria-hidden="true" />;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== isOpen || $[13] !== listId || $[14] !== t8) {
    t9 = <button type="button" onClick={t5} className="t-flex t-w-full t-items-center t-justify-between t-gap-3 t-bg-white t-px-6 t-py-[18px] t-text-left t-text-sm t-font-extrabold t-text-[#38323d] hover:t-bg-[#faf9fb] hover:t-text-brand-violet focus:t-outline-none focus:t-ring-2 focus:t-ring-inset focus:t-ring-brand-violet t-transition-colors" aria-expanded={isOpen} aria-controls={listId}>{t6}{t8}</button>;
    $[12] = isOpen;
    $[13] = listId;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== collections || $[17] !== currentSlug || $[18] !== isOpen || $[19] !== listId || $[20] !== onNavigate) {
    t10 = isOpen && <div id={listId} className="t-border-t t-border-[#e4e0e6] t-bg-white t-px-6 t-pb-4 t-pt-2"><ul><li><Link href="/cars" {...onNavigate ? {
            onClick: onNavigate
          } : {}} {...!currentSlug ? {
            "aria-current": "page" as const
          } : {}} className={`t-relative t-block t-rounded-md t-px-2 t-py-2 t-text-[13px] t-transition-colors focus:t-outline-none focus:t-ring-2 focus:t-ring-inset focus:t-ring-brand-violet ${!currentSlug ? "t-bg-[#f7f3ff] t-font-semibold t-text-brand-violet-dark" : "t-font-medium t-text-[#5e5864] hover:t-bg-[#faf9fb] hover:t-text-brand-violet"}`}>Shop All Vehicles</Link></li>{collections.slice(0, 4).map(collection => {
          const isCurrent = collection.slug === currentSlug;
          return <li key={collection.id}><Link href={`/cars/collections/${collection.slug}`} {...onNavigate ? {
              onClick: onNavigate
            } : {}} {...isCurrent ? {
              "aria-current": "page" as const
            } : {}} className={`t-relative t-block t-rounded-md t-px-2 t-py-2 t-text-[13px] t-transition-colors focus:t-outline-none focus:t-ring-2 focus:t-ring-inset focus:t-ring-brand-violet ${isCurrent ? "t-bg-[#f7f3ff] t-font-semibold t-text-brand-violet-dark" : "t-font-medium t-text-[#5e5864] hover:t-bg-[#faf9fb] hover:t-text-brand-violet"}`}>Shop {collection.name}</Link></li>;
        })}</ul>{collections.length > 4 && <Link href="/cars/collections" {...onNavigate ? {
        onClick: onNavigate
      } : {}} className="t-mx-8 t-mt-1 t-inline-flex t-rounded t-text-xs t-font-semibold t-text-automotive-primary hover:t-text-automotive-secondary focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2">View all collections</Link>}</div>;
    $[16] = collections;
    $[17] = currentSlug;
    $[18] = isOpen;
    $[19] = listId;
    $[20] = onNavigate;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== headingId || $[23] !== t10 || $[24] !== t3 || $[25] !== t4 || $[26] !== t9) {
    t11 = <nav className="t-border-b t-border-[#e4e0e6] t-bg-white" aria-labelledby={headingId}>{t3}{t4}{t9}{t10}</nav>;
    $[22] = headingId;
    $[23] = t10;
    $[24] = t3;
    $[25] = t4;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
function _temp(open) {
  return !open;
}